import { useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Map, Copy, Check } from 'lucide-react';

export default function NeighborhoodIntel() {
  const [form, setForm] = useState({ neighborhood: '', city: '', focus: '' });
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true); setOutput('');
    try {
      const { data } = await http.post('/ai/market', form);
      setOutput(data.output);
      toast.success('Intel brief ready.');
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };

  const copy = () => { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Neighborhood Intel</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="intel-title">Hyper-local Market Briefs</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Instant briefs for any neighborhood — snapshot, momentum, buyer profile, and 3 talking points
          for your next client call.
        </p>
        <div
          className="mt-5 p-4 max-w-3xl text-xs"
          style={{ background: 'var(--parchment)', border: '1px solid var(--gold)', borderRadius: 2, color: 'var(--ink-soft)', lineHeight: 1.6 }}
          data-testid="intel-disclaimer"
        >
          <div className="hq-eyebrow mb-2" style={{ color: 'var(--gold-soft)' }}>AI-Generated · General Market Insight</div>
          These briefs are produced by AI from general market knowledge — they are <strong>not</strong> live MLS or transaction data.
          Any prices, days-on-market, or trend figures are directional estimates and must be verified against your MLS, broker
          metrics report, or a licensed appraiser before quoting to a client.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={run} className="hq-card p-8 space-y-5">
          <div><label className="hq-label">Neighborhood</label>
            <input className="hq-input" required data-testid="intel-neighborhood" value={form.neighborhood} onChange={e => setForm({...form, neighborhood: e.target.value})} placeholder="Beacon Hill" />
          </div>
          <div><label className="hq-label">City / Region</label>
            <input className="hq-input" required data-testid="intel-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Boston, MA" />
          </div>
          <div><label className="hq-label">Focus (optional)</label>
            <input className="hq-input" value={form.focus} onChange={e => setForm({...form, focus: e.target.value})} placeholder="Historic single-family, luxury condos, first-time buyer…" />
          </div>
          <button className="hq-btn-primary w-full" disabled={loading} data-testid="run-intel-btn">
            {loading ? 'Analyzing…' : <><Map size={12} className="inline mr-2"/> Generate Intel</>}
          </button>
        </form>

        <div className="hq-card p-8 min-h-[400px]">
          <div className="flex justify-between items-center mb-3">
            <span className="hq-eyebrow">Brief</span>
            {output && (
              <button onClick={copy} className="hq-btn-ghost" data-testid="copy-intel-btn">
                {copied ? <><Check size={12} className="inline mr-1"/> Copied</> : <><Copy size={12} className="inline mr-1"/> Copy</>}
              </button>
            )}
          </div>
          {loading && <div className="hq-shimmer h-80"></div>}
          {!loading && !output && (
            <div className="text-center py-24" style={{ color: 'var(--muted)' }}>
              <Map size={32} className="mx-auto mb-4" style={{ color: 'var(--gold)' }} />
              <div className="font-serif text-xl" style={{ color: 'var(--sage)' }}>Choose a neighborhood.</div>
            </div>
          )}
          {output && (
            <pre className="whitespace-pre-wrap font-serif text-base hq-fade-in" style={{ color: 'var(--ink)', lineHeight: 1.75 }} data-testid="intel-output">
              {output}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
